import { useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import {
  Alert, Box, Chip, Collapse, Divider, Link, Paper, Stack, Tooltip, Typography,
  useTheme,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import type { Covering } from '../api/types';
import { coveringDate, planHref } from '../util/coverage';
import { useShowPeople, useWhom } from '../features/people/whom';

/*
 * What is known about ONE place: who has planned it, how often, and in which
 * saved work.
 *
 * Its own component because the question is asked in two places and must be
 * answered the same way in both. A traveller choosing places asks it of a POI
 * before anything is planned — "has this ground been worked, and by whom?" —
 * and a traveller reading a planned round asks it of a stop. The round adds
 * context (work planned for later, a map, a stop number) and takes nothing
 * away, so the context lives in the callers and the record lives here.
 */

/** A date as the record holds it (YYYY-MM-DD), for reading. */
export function onDay(at: string | null): string {
  if (!at) return '';
  const when = new Date(at.slice(0, 10));
  if (Number.isNaN(when.getTime())) return '';

  return when.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** The newest of a set of dates, as the record holds them. */
function latest(dates: string[]): string {
  return dates.filter(Boolean).sort().at(-1) ?? '';
}

/** Whole days between two ISO dates. */
const daysBetween = (from: string, to: string) => Math.round(
  (new Date(to).getTime() - new Date(from).getTime()) / (1000 * 60 * 60 * 24),
);

/**
 * How often a place gets planned, from the dates the plans are FOR.
 *
 * Counts and dates separately answer "how much" and "when"; neither answers
 * "is this somewhere people keep going back to". Eight plans across five
 * weeks is a regular call; eight across two years is a place that was busy
 * once. The average gap between planned days says which, in one phrase.
 *
 * Measured on distinct DAYS rather than on plans, because three plans for the
 * same morning is one occasion as far as cadence goes — and from the planned
 * dates rather than the save times, since when somebody typed a plan in says
 * nothing about when the ground is worked.
 *
 * `days` comes back with it so the chart behind the figure plots exactly the
 * set that produced the number.
 */
function cadence(dates: string[]): { rate: string; span: string; days: string[] } {
  const days = [...new Set(dates.filter(Boolean))].sort();

  if (days.length === 0) return { rate: '—', span: 'no dates set', days };

  const first = days[0];
  const last = days[days.length - 1];
  const span = first === last ? onDay(first) : `${onDay(first)} – ${onDay(last)}`;

  if (days.length === 1) return { rate: 'once', span, days };

  const apart = Math.round(daysBetween(first, last) / (days.length - 1));

  if (apart <= 1) return { rate: 'most days', span, days };
  if (apart <= 10) return { rate: `every ${apart} days`, span, days };
  if (apart <= 45) return { rate: `every ${Math.round(apart / 7)} weeks`, span, days };

  return { rate: `every ${Math.round(apart / 30)} months`, span, days };
}

/**
 * What "every N days" is actually made of.
 *
 * The figure alone cannot tell three visits in one week followed by nothing
 * for a year apart from one visit a month — both average out the same, and
 * only one of them means the place is in regular use. So the detail is the
 * shape: where the planned days fall, and how uneven the gaps between them
 * are.
 *
 * Drawn as plain SVG. The data is a handful of dates on one axis; a charting
 * library would be more bytes than the whole panel and no clearer.
 */
function CadenceChart({ days }: { days: string[] }) {
  const theme = useTheme();

  if (days.length === 0) return null;

  const first = days[0];
  const last = days[days.length - 1];
  const total = Math.max(1, daysBetween(first, last));
  const gaps = days.slice(1).map((day, at) => daysBetween(days[at], day));
  const mean = gaps.length ? gaps.reduce((sum, n) => sum + n, 0) / gaps.length : 0;
  const widest = Math.max(...gaps, 1);

  /*
   * A fixed viewBox scaled to the container, so the text scales with the
   * drawing instead of being stretched by it.
   */
  const W = 600;
  const PAD = 34;
  const span = W - PAD * 2;
  const at = (day: string) => PAD + (daysBetween(first, day) / total) * span;

  /*
    Colours from theme.vars, and applied through `style`.

    Two traps, both of which make this chart invisible on the dark theme and
    perfect on the light one. The theme is built with cssVariables, so
    `theme.palette` keeps the literal values of the DEFAULT scheme while the
    variables that follow the chosen scheme live on `theme.vars`. And a CSS
    variable resolves in a style declaration but NOT in an SVG presentation
    attribute, where the browser drops the value and falls back to black.
  */
  const colours = theme.vars?.palette ?? theme.palette;
  const ink = colours.text.primary;
  const quiet = colours.text.secondary;
  const faint = colours.divider;
  const mark = colours.primary.main;
  const warn = colours.warning.main;
  const paint = (fill: string) => ({ fill });
  const draw = (stroke: string, width: number, dashed = false) => ({
    stroke,
    strokeWidth: width,
    ...(dashed ? { strokeDasharray: '4 3' } : {}),
  });

  return (
    <Box sx={{ mt: 1 }}>
      <svg
        viewBox={`0 0 ${W} ${gaps.length ? 128 : 64}`}
        width="100%"
        role="img"
        aria-label={`Planned on ${days.length} distinct days between ${onDay(first)} and ${onDay(last)}`}
      >
        {/* The days themselves, spaced by real time rather than evenly — the
            clustering is the whole point, and an even row would hide it. */}
        <line x1={PAD} y1={20} x2={W - PAD} y2={20} style={draw(faint, 2)} />
        {days.map((day) => (
          <circle key={day} cx={at(day)} cy={20} r={5} style={paint(mark)}>
            <title>{onDay(day)}</title>
          </circle>
        ))}
        <text x={PAD} y={42} fontSize={12} style={paint(quiet)} textAnchor="start">
          {onDay(first)}
        </text>
        {last !== first && (
          <text x={W - PAD} y={42} fontSize={12} style={paint(quiet)} textAnchor="end">
            {onDay(last)}
          </text>
        )}

        {gaps.length > 0 && (
          <>
            <text x={PAD} y={68} fontSize={12} style={paint(ink)} fontWeight={600}>
              Gap between one planned day and the next
            </text>
            {gaps.map((gap, i) => {
              const barW = Math.max(3, (span / gaps.length) - 6);
              const x = PAD + i * (span / gaps.length);
              /* A floor of six, not two: a one-day gap beside an eleven-day
                 one came out as a hairline that read as no bar at all. */
              const h = Math.max(6, (gap / widest) * 36);

              return (
                <rect
                  key={`${days[i]}-${days[i + 1]}`}
                  x={x}
                  y={116 - h}
                  width={barW}
                  height={h}
                  rx={2}
                  style={paint(gap > mean ? warn : mark)}
                  opacity={0.9}
                >
                  <title>{`${gap} ${gap === 1 ? 'day' : 'days'} — ${onDay(days[i])} to ${onDay(days[i + 1])}`}</title>
                </rect>
              );
            })}
            {/* The average, drawn across the bars and named above them: on its
                own line it collided with whichever bar reached that end. */}
            <line
              x1={PAD}
              y1={116 - Math.max(6, (mean / widest) * 36)}
              x2={W - PAD}
              y2={116 - Math.max(6, (mean / widest) * 36)}
              style={draw(ink, 1.5, true)}
              opacity={0.8}
            />
            <text x={W - PAD} y={68} fontSize={11} style={paint(quiet)} textAnchor="end">
              {`dashed line: average ${Math.round(mean)} ${Math.round(mean) === 1 ? 'day' : 'days'}`}
            </text>
            <line x1={PAD} y1={116} x2={W - PAD} y2={116} style={draw(faint, 1)} />
          </>
        )}
      </svg>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {gaps.length > 0
          ? `${days.length} distinct days · shortest gap ${Math.min(...gaps)}, longest ${widest}. Bars above the line are the longer waits.`
          : 'Planned on one day only, so there is no gap to measure.'}
      </Typography>
    </Box>
  );
}

/** A figure of the strip, with the rule behind it one hover away. */
function Figure({ label, value, hint, under, onClick, open }: {
  label: string;
  value: string;
  hint: string;
  under?: string;
  /** Present where the figure folds something out below the strip. */
  onClick?: () => void;
  open?: boolean;
}) {
  return (
    <Tooltip title={hint} enterDelay={400} arrow>
      <Box
        onClick={onClick}
        sx={{ minWidth: 0, cursor: onClick ? 'pointer' : 'help' }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', lineHeight: 1.4 }}
          noWrap
        >
          {label}
        </Typography>
        <Typography variant="h3" sx={{ lineHeight: 1.3, my: 0.25 }}>
          {value}
          {onClick && (
            <ExpandMoreIcon
              sx={{
                fontSize: 16,
                verticalAlign: 'middle',
                ml: 0.25,
                color: 'text.disabled',
                transform: open ? 'rotate(180deg)' : 'none',
                transition: 'transform 150ms',
              }}
            />
          )}
        </Typography>
        {under && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: 'block', lineHeight: 1.35 }}
          >
            {under}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

/**
 * What one person's work on this place adds up to.
 *
 * Counted two ways because they answer different questions: how many of their
 * saved plans take it in, and how many stops those plans make there (an
 * operation that goes back on Thursday makes two).
 */
interface PersonRecord {
  by: string;
  byName: string;
  plans: number;
  stops: number;
  last: string;
}

function peopleIn(coverings: Covering[]): PersonRecord[] {
  const found = new Map<string, PersonRecord & { records: Set<string> }>();

  for (const c of coverings) {
    const key = c.by.toLowerCase();
    const seen = found.get(key) ?? {
      by: c.by, byName: c.byName, plans: 0, stops: 0, last: '', records: new Set<string>(),
    };

    seen.records.add(c.id);
    seen.stops += 1;

    const on = coveringDate(c);
    if (on && on > seen.last) seen.last = on;

    found.set(key, seen);
  }

  return [...found.values()]
    .map(({ records, ...rest }) => ({ ...rest, plans: records.size }))
    .sort((a, b) => b.stops - a.stops || b.plans - a.plans);
}

/**
 * What that plan does here, in the words a reader would use.
 *
 * "No time set" is true of every stay of zero and explains nothing: the
 * commonest reason by far is that the place is where the day BEGINS. Between
 * two other stops a zero means the opposite — the round goes past without
 * stopping, which is a thing somebody chose.
 */
function stayIn(covering: Covering): string {
  if (covering.dwellMinutes > 0) return `${covering.dwellMinutes} min planned`;

  const last = covering.planStops > 0 && covering.stopIndex === covering.planStops;
  if (covering.stopIndex === 1 && last) return 'starts and ends here';
  if (covering.stopIndex === 1) return 'starts here';
  if (last) return 'ends here';

  return 'passes by, no stop';
}

/**
 * How well covered a place is, in words rather than a number to interpret.
 *
 * The thresholds are stated in the tooltip because this deployment has no
 * configured coverage target: the screen must not imply a rule somebody set
 * when it is applying one chosen in a component. Two plans by two people is
 * the line — one plan is one person's intention, which says little about
 * whether the ground is generally worked.
 */
function standing(
  { records, people, later }: { records: number; people: number; later: number },
): { label: string; colour: 'default' | 'success' | 'warning'; why: string } {
  if (records === 0) {
    return later > 0
      ? {
        label: 'Not covered yet',
        colour: 'warning',
        why: 'No saved plan made before this round takes it in. It is in saved work for this round’s own date or later, listed below: two plans want the same place.',
      }
      : {
        label: 'Not covered',
        colour: 'warning',
        why: 'In no saved plan. New ground as far as the saved record goes — which is not the same as never visited, because nothing here records visits.',
      };
  }

  if (records === 1 || people <= 1) {
    return {
      label: 'Thinly covered',
      colour: 'default',
      why: `In ${records === 1 ? 'one saved plan' : `${records} saved plans`} by ${people === 1 ? 'one planner' : `${people} planners`}. Counted as thin because a single plan, or a single person’s plans, is one intention rather than ground that is generally worked. This deployment sets no coverage target; the rule is this panel’s own.`,
    };
  }

  return {
    label: 'Covered',
    colour: 'success',
    why: `In ${records} saved plans by ${people} planners. The rule is this panel’s own — two or more plans from two or more people — because this deployment sets no coverage target.`,
  };
}

/**
 * How many appearances are drawn before the list asks for more.
 *
 * A well-worked place can be in hundreds of saved plans, and each is a Paper
 * with a link and a collapse inside it. A page at a time costs what is read.
 */
const PAGE = 20;

/**
 * One plan that takes this place in, openable for how it does so.
 *
 * Shut, it says which plan and when. Opened, it says where in the day the
 * place sits and what the plan does on either side of it — the difference
 * between "covered" and "covered properly", without leaving this window.
 */
function Appearance({ covering, later, who }: {
  covering: Covering;
  later: boolean;
  /** Whose plan this is, or null when the list is already pinned to one person. */
  who: string | null;
}) {
  const [open, setOpen] = useState(false);
  const day = covering.day > 0 ? `Day ${covering.day}` : '';

  return (
    <Paper variant="outlined" sx={{ px: 1.25, py: 0.75 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center', cursor: 'pointer' }}
        onClick={() => setOpen((shown) => !shown)}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
            <Link
              href={planHref(covering.id, covering.source, covering.day)}
              target="_blank"
              rel="noopener"
              underline="hover"
              color="inherit"
              variant="body2"
              onClick={(e) => e.stopPropagation()}
            >
              {covering.name || (covering.source === 'operation' ? 'an operation' : 'a saved route')}
              <OpenInNewIcon sx={{ fontSize: 12, ml: 0.25, verticalAlign: 'middle' }} />
            </Link>
            {later && (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label="planned later"
                sx={{ height: 18, fontSize: 11 }}
              />
            )}
          </Stack>

          <Typography variant="caption" color="text.secondary">
            {[
              who,
              [day, onDay(coveringDate(covering))].filter(Boolean).join(' · '),
              covering.planTime,
              stayIn(covering),
              covering.metres > 0 ? `${Math.round(covering.metres)} m away` : '',
            ].filter(Boolean).join(' · ')}
          </Typography>
        </Box>

        <ExpandMoreIcon
          fontSize="small"
          color="action"
          sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}
        />
      </Stack>

      <Collapse in={open} unmountOnExit>
        <Divider sx={{ my: 0.75 }} />
        <Stack spacing={0.25} sx={{ pb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {covering.planStops > 0
              ? `Stop ${covering.stopIndex} of ${covering.planStops} that day`
              : `Stop ${covering.stopIndex} that day`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {covering.prevStop ? `Arrives from ${covering.prevStop}` : 'The day starts here'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {covering.nextStop ? `Goes on to ${covering.nextStop}` : 'The day ends here'}
          </Typography>
        </Stack>
      </Collapse>
    </Paper>
  );
}

export function PlaceCoverageReport({
  coverings, later = [], resetKey, scrollRoot,
}: {
  /** Saved work that covers this place. In a round: planned before it. */
  coverings: Covering[];
  /** Saved work for the round's own date or later. Empty outside a round. */
  later?: Covering[];
  /** Changing it starts the filter and the paging again: a different place. */
  resetKey?: string;
  /** The element that scrolls, so the list can page as its foot nears the end. */
  scrollRoot?: RefObject<HTMLDivElement | null>;
}) {
  const whom = useWhom();
  const showPeople = useShowPeople();

  const people = useMemo(() => peopleIn([...coverings, ...later]), [coverings, later]);

  /*
    One person's work, when somebody picks a name.

    Held as the username, because that is what identifies the account; two
    people may share a display name.
  */
  const [only, setOnly] = useState<string | null>(null);
  /* The "How often" figure folds a chart out below the strip. Shut by
     default: the figure answers most visits, the picture the rest. */
  const [cadenceOpen, setCadenceOpen] = useState(false);
  const [shown, setShown] = useState(PAGE);
  const foot = useRef<HTMLDivElement | null>(null);

  const everyAppearance = useMemo(() => [
    ...coverings.map((c) => ({ covering: c, later: false })),
    ...later.map((c) => ({ covering: c, later: true })),
  ], [coverings, later]);

  const appearances = useMemo(() => (
    only === null
      ? everyAppearance
      : everyAppearance.filter(
        ({ covering }) => covering.by.toLowerCase() === only.toLowerCase(),
      )
  ), [everyAppearance, only]);

  useEffect(() => { setShown(PAGE); }, [resetKey, only]);
  useEffect(() => { setOnly(null); setCadenceOpen(false); }, [resetKey]);

  /*
    More, as the end of the list comes into view.

    An observer rather than a scroll handler: it fires once when the foot
    appears instead of on every pixel, and the margin asks for the next page
    while the reader is still two hundred pixels from the end of this one.
  */
  useEffect(() => {
    const node = foot.current;
    if (!node || appearances.length <= shown) return undefined;

    const watcher = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShown((drawn) => drawn + PAGE);
        }
      },
      { root: scrollRoot?.current ?? null, rootMargin: '200px' },
    );
    watcher.observe(node);

    return () => watcher.disconnect();
  }, [appearances.length, shown, scrollRoot]);

  const records = useMemo(
    () => new Set(coverings.map((c) => c.id)).size,
    [coverings],
  );
  const occurrences = coverings.length;
  const covered = occurrences > 0;
  const state = standing({ records, people: people.length, later: later.length });
  const lastPlannedFor = latest(coverings.map(coveringDate));
  /*
    Over everything this place is in, work planned for later included, and
    deliberately NOT narrowed by whoever is picked below: the figures describe
    the place, and one of them quietly answering about one person while the
    rest answer about everybody is worse than either.
  */
  const often = cadence(everyAppearance.map(({ covering }) => coveringDate(covering)));

  /* What the list below holds, after any name has been picked. One row per
     STOP, not per plan, which is why the count can exceed the plan count. */
  const laterShown = appearances.filter((a) => a.later).length;
  const beforeShown = appearances.length - laterShown;
  const plansShown = new Set(appearances.map(({ covering }) => covering.id)).size;

  return (
    <Box>
      <Stack direction="row" spacing={0.75} sx={{ mb: 1.5, flexWrap: 'wrap' }} useFlexGap>
        <Tooltip title={state.why} enterDelay={300} arrow>
          <Chip
            size="small"
            color={state.colour}
            variant={state.colour === 'default' ? 'outlined' : 'filled'}
            icon={<InfoOutlinedIcon />}
            label={state.label}
            sx={{ height: 22, cursor: 'help' }}
          />
        </Tooltip>
      </Stack>

      {/*
        The figures on a grid rather than a wrapping row: columns of equal
        width share the panel however many there are, so the labels line up
        and the strip reads as one band rather than as several loose
        captions.
      */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(104px, 1fr))',
          columnGap: 2,
          rowGap: 1.5,
          alignItems: 'start',
          mb: 2,
          pb: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Figure
          label="Saved plans"
          value={String(records)}
          under={records === 1 ? 'takes it in' : 'take it in'}
          hint="Unique saved routes and operations that include this place as a stop. One counts once, however many of its days stop here."
        />
        {showPeople && (
          <Figure
            label="Planners"
            value={String(people.length)}
            under={people.length === 1 ? 'one person' : 'different people'}
            hint="Distinct accounts that SAVED those plans. Not who went: nothing here records a visit."
          />
        )}
        {/* Stops, only where that is a different number from plans: otherwise
            two figures reading "1" say the same thing twice. */}
        {occurrences !== records && (
          <Figure
            label="Planned stops"
            value={String(occurrences)}
            under={`across ${records} ${records === 1 ? 'plan' : 'plans'}`}
            hint="Times this place appears as a stop in all, including repeat days within one plan."
          />
        )}
        <Figure
          label="Last planned for"
          value={onDay(lastPlannedFor) || '—'}
          under="the day itself"
          hint="The latest date any of those plans is planned FOR, whenever it was saved."
        />
        <Figure
          label="How often"
          value={often.rate}
          under={often.span}
          hint="The average number of days between the distinct dates this place is planned for."
          onClick={often.days.length > 0 ? () => setCadenceOpen((open) => !open) : undefined}
          open={cadenceOpen}
        />
      </Box>

      <Collapse in={cadenceOpen} unmountOnExit>
        <CadenceChart days={often.days} />
      </Collapse>

      {!covered && (
        <Alert
          severity={later.length > 0 ? 'warning' : 'info'}
          variant="outlined"
          sx={{ mb: 2 }}
        >
          {later.length > 0
            ? 'No saved plan made before this round takes it in — but it is in saved work for this round’s own date or later, listed below.'
            : 'No saved route or operation stops here. New ground, as far as the saved record goes.'}
        </Alert>
      )}

      {showPeople && people.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h3" sx={{ mb: 1 }}>Who has planned it</Typography>
          <Stack spacing={0.75}>
            {people.map((person) => {
              const picked = only !== null && person.by.toLowerCase() === only.toLowerCase();

              return (
                <Paper
                  key={person.by || '(unattributed)'}
                  variant="outlined"
                  onClick={() => setOnly(picked ? null : person.by)}
                  sx={{
                    px: 1.25,
                    py: 0.75,
                    cursor: 'pointer',
                    borderColor: picked ? 'primary.main' : 'divider',
                    bgcolor: picked ? 'action.selected' : 'transparent',
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}
                    useFlexGap
                  >
                    {picked && (
                      <CheckIcon
                        sx={{ fontSize: 15, color: 'primary.main', alignSelf: 'center' }}
                      />
                    )}
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, flex: 1, minWidth: 120 }}
                      color={person.by ? 'text.primary' : 'text.secondary'}
                    >
                      {whom(person.by, person.byName)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[
                        `${person.plans} ${person.plans === 1 ? 'plan' : 'plans'}`,
                        person.stops !== person.plans ? `${person.stops} stops` : null,
                        onDay(person.last) ? `last ${onDay(person.last)}` : null,
                      ].filter(Boolean).join(' · ')}
                    </Typography>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.5 }}
          >
            {only === null
              ? 'Pick a name to see that person’s plans alone.'
              : 'Showing one person’s plans. Pick the same name again for everybody.'}
          </Typography>
        </Box>
      )}

      {(covered || later.length > 0) && (
        <Box>
          <Divider sx={{ mb: 1.5 }} />
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ alignItems: 'center', mb: 1, flexWrap: 'wrap' }}
            useFlexGap
          >
            <Typography variant="h3">In these saved plans</Typography>
            <Tooltip
              title="Open one for where this place sits in its day, and what the plan does either side of it."
              enterDelay={400}
              arrow
            >
              <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled', cursor: 'help' }} />
            </Tooltip>
            <Box sx={{ flex: 1 }} />
            {only !== null && (
              <Chip
                size="small"
                label={whom(only, people.find((p) => p.by === only)?.byName)}
                onDelete={() => setOnly(null)}
                sx={{ height: 22 }}
              />
            )}
            <Tooltip
              title="One row per stop. Where a round is being analysed, the figures above count only work planned before it while this list also shows plans for its own date or later, which are marked."
              enterDelay={300}
              arrow
            >
              <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                {appearances.length > shown
                  ? `${Math.min(shown, appearances.length)} of ${appearances.length} stops`
                  : [
                    appearances.length === plansShown
                      ? `${plansShown} ${plansShown === 1 ? 'plan' : 'plans'}`
                      : `${appearances.length} stops in ${plansShown} plans`,
                    laterShown > 0 && beforeShown > 0 ? `${laterShown} later` : '',
                  ].filter(Boolean).join(' · ')}
              </Typography>
            </Tooltip>
          </Stack>

          <Stack spacing={0.75}>
            {appearances.slice(0, shown).map(({ covering, later: ahead }, at) => (
              <Appearance
                key={`${covering.id}-${covering.day}-${ahead ? 'later' : 'before'}-${at}`}
                covering={covering}
                later={ahead}
                who={showPeople && only === null ? whom(covering.by, covering.byName) : null}
              />
            ))}
            {appearances.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Nothing of theirs takes this place in.
              </Typography>
            )}
            {/* The foot of the list, watched rather than clicked. See PAGE. */}
            <Box ref={foot} sx={{ height: 1 }} />
          </Stack>
        </Box>
      )}
    </Box>
  );
}
